// API service for communicating with the backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to handle fetch requests
const fetchWithAuth = async (url, options = {}) => {
  try {
    // Get token from localStorage
    const token = localStorage.getItem('pscToken');
    
    // Set headers
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    // Add auth token if it exists
    if (token) {
      headers['x-auth-token'] = token;
    }
    
    // Make request
    const response = await fetch(`${API_URL}${url}`, {
      ...options,
      headers,
    });
    
    // Parse JSON response
    const data = await response.json();
    
    // Check if response is ok
    if (!response.ok) {
      const error = new Error(data.message || 'Une erreur est survenue');
      error.status = response.status;
      error.data = data;
      throw error;
    }
    
    return data;
  } catch (error) {
    // Handle network errors or JSON parsing errors
    if (!error.status) {
      console.error('Erreur réseau:', error);
      throw new Error('Impossible de se connecter au serveur. Veuillez vérifier votre connexion internet.');
    }
    throw error;
  }
};

// Auth API
export const authAPI = {
  // Register a new user
  register: async (userData) => {
    return fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },
  
  // Login user
  login: async (credentials) => {
    return fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },
  
  // Verify email
  verifyEmail: async (token) => {
    return fetchWithAuth(`/auth/verify-email?token=${token}`);
  },
  
  // Forgot password
  forgotPassword: async (email) => {
    return fetchWithAuth('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
  
  // Reset password
  resetPassword: async (token, password) => {
    return fetchWithAuth('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },
};

// User API
export const userAPI = {
  // Get user profile
  getProfile: async () => {
    return fetchWithAuth('/user/profile');
  },
  
  // Update user profile
  updateProfile: async (profileData) => {
    return fetchWithAuth('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },
  
  // Change password
  changePassword: async (passwordData) => {
    return fetchWithAuth('/user/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  },
  
  // Get partner requests
  getPartnerRequests: async () => {
    return fetchWithAuth('/user/partner-requests');
  },
};

// Registration API
export const registrationAPI = {
  // Create a new registration
  createRegistration: async (registrationData) => {
    return fetchWithAuth('/registration', {
      method: 'POST',
      body: JSON.stringify(registrationData),
    });
  },
  
  // Get user registration
  getMyRegistration: async () => {
    return fetchWithAuth('/registration/my-registration');
  },
  
  // Update profile and WSDC information
  updateProfile: async (profileData) => {
    return fetchWithAuth('/registration/update-profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },
  
  // Accept partner request
  acceptPartner: async (partnerData) => {
    return fetchWithAuth('/registration/accept-partner', {
      method: 'POST',
      body: JSON.stringify(partnerData),
    });
  },
};

// Competition API
export const competitionAPI = {
  // Get all competitions
  getCompetitions: async () => {
    return fetchWithAuth('/competition');
  },
  
  // Get competition by ID
  getCompetitionById: async (id) => {
    return fetchWithAuth(`/competition/${id}`);
  },
  
  // Register for a competition
  registerForCompetition: async (competitionData) => {
    return fetchWithAuth('/competition/register', {
      method: 'POST',
      body: JSON.stringify(competitionData),
    });
  },
  
  // Accept competition partner request
  acceptPartner: async (partnerData) => {
    return fetchWithAuth('/competition/accept-partner', {
      method: 'POST',
      body: JSON.stringify(partnerData),
    });
  },
  
  // Cancel competition registration
  cancelRegistration: async (id) => {
    return fetchWithAuth(`/competition/${id}`, {
      method: 'DELETE',
    });
  },

  // Create a new competition (admin only)
  createCompetition: async (competitionData) => {
    return fetchWithAuth('/competition', {
      method: 'POST',
      body: JSON.stringify(competitionData),
    });
  },

  // Update a competition (admin only)
  updateCompetition: async (id, competitionData) => {
    return fetchWithAuth(`/competition/${id}`, {
      method: 'PUT',
      body: JSON.stringify(competitionData),
    });
  },

  // Delete a competition (admin only)
  deleteCompetition: async (id) => {
    return fetchWithAuth(`/competition/${id}/admin`, {
      method: 'DELETE',
    });
  },

  // Toggle competition status (admin only)
  toggleCompetitionStatus: async (id, actif) => {
    return fetchWithAuth(`/competition/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ actif }),
    });
  },

  // Get competition participants (admin only)
  getCompetitionParticipants: async (id) => {
    return fetchWithAuth(`/competition/${id}/participants`);
  },
};

// Workshop API
export const workshopAPI = {
  // Get all workshops
  getWorkshops: async () => {
    return fetchWithAuth('/workshop');
  },
  
  // Register for a workshop
  registerForWorkshop: async (workshopData) => {
    return fetchWithAuth('/workshop/register', {
      method: 'POST',
      body: JSON.stringify(workshopData),
    });
  },
  
  // Cancel workshop registration
  cancelRegistration: async (id) => {
    return fetchWithAuth(`/workshop/${id}`, {
      method: 'DELETE',
    });
  },
};

// Pass API
export const passAPI = {
  // Get all pass offers
  getPassOffers: async () => {
    return fetchWithAuth('/passes');
  },
  
  // Get pass offer by ID
  getPassOfferById: async (id) => {
    return fetchWithAuth(`/passes/${id}`);
  },
  
  // Purchase a pass
  purchasePass: async (passData) => {
    return fetchWithAuth('/passes/purchase', {
      method: 'POST',
      body: JSON.stringify(passData),
    });
  },
  
  // Get user passes
  getUserPasses: async () => {
    return fetchWithAuth('/passes/user/me');
  },

  // Create a new pass (admin only)
  createPass: async (passData) => {
    return fetchWithAuth('/passes', {
      method: 'POST',
      body: JSON.stringify(passData),
    });
  },

  // Update a pass (admin only)
  updatePass: async (id, passData) => {
    return fetchWithAuth(`/passes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(passData),
    });
  },

  // Delete a pass (admin only)
  deletePass: async (id) => {
    return fetchWithAuth(`/passes/${id}`, {
      method: 'DELETE',
    });
  },

  // Toggle pass status (admin only)
  togglePassStatus: async (id, actif) => {
    return fetchWithAuth(`/passes/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ actif }),
    });
  },
};

// Group API
export const groupAPI = {
  // Verify group code
  verifyGroupCode: async (code) => {
    return fetchWithAuth('/groups/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },
  
  // Join group
  joinGroup: async (groupId) => {
    return fetchWithAuth('/groups/join', {
      method: 'POST',
      body: JSON.stringify({ groupId }),
    });
  },
  
  // Get user groups
  getUserGroups: async () => {
    return fetchWithAuth('/groups/user/me');
  },
};

export default {
  auth: authAPI,
  user: userAPI,
  registration: registrationAPI,
  competition: competitionAPI,
  workshop: workshopAPI,
  pass: passAPI,
  group: groupAPI,
};
